import { normalizeInput } from './movement.mjs';

const INPUT_INTERVAL = 100;
const PRESENCE_INTERVAL = 1000 / 3;
const MAX_JUMP_EDGES = 8;
const MAX_RECENT_IDS = 256;
const validSeq = value => Number.isSafeInteger(value) && value >= 0;
const validId = value => typeof value === 'string' && value.length > 0;
const problem = (message, code) => Object.assign(new Error(message), { code });

// Mark fire-and-forget frame sends as observed, without changing what an await
// sees. In particular, action failures must still reject to the UI caller.
function observed(promise) { promise.catch(() => {}); return promise; }
function deferred() {
  let resolve, reject;
  const promise = observed(new Promise((yes, no) => { resolve = yes; reject = no; }));
  return { promise, resolve, reject };
}

/** Hosted HTTP adapter. The Node service keeps its separate WebSocket path.
 *
 * api(path, body, 'POST') must reject unsuccessful HTTP responses. send() returns
 * a promise: samples coalesced into one pending batch share its eventual result.
 * onConnection receives (connected, errorOrNull); only a successful API response
 * establishes connectivity. Duration input retries preserve immutable ordered samples; legacy inputs retain
 * only the newest movement plus eight jump transitions. Oversized frame history
 * requests an explicit authority rebase. Operations are never automatically replayed.
 * A hidden tab makes no presence requests. Its existing request is ignored if
 * it completes while hidden. Polling resumes on the next visibility check.
 */
export function createHttpWorldTransport({
  api, onMessage = () => {}, onConnection = () => {},
  isVisible = () => typeof document === 'undefined' || !document.hidden,
  now = () => performance.now(),
  schedule = (run, delay) => setTimeout(run, delay),
  cancel = id => clearTimeout(id),
  retryBaseMs = 250, retryMaxMs = 4000,
} = {}) {
  if (typeof api !== 'function') throw new TypeError('HTTP world transport requires api');
  let disposed = false, connected = null, connectionError = null;
  let latestInput = null, pendingInput = null, lastQueuedSeq = -1;
  let inputFrames = [], inputResync = false;
  const MAX_INPUT_FRAMES = 256;
  let jumpEdges = [], lastQueuedJump = false, sentJump = false;
  let inputFlight = false, inputTimer = null, inputSentAt = -Infinity, activeInputBatch = null;
  let latestInputAt = -Infinity;
  let inputFailures = 0, inputRetryAt = -Infinity, lastAppliedSeq = -1;
  let activeWorldId = null, worldGeneration = 0;
  let interest = null, interestGeneration = 0, pendingPresence = null;
  let presenceFlight = false, presenceTimer = null, presenceSentAt = -Infinity, activePresenceBatch = null;
  let presenceFailures = 0, presenceRetryAt = -Infinity, lastSnapshotSeq = -1;
  let eventCursor;
  const eventIds = new Set(), crossingIds = new Set(), actionWaiters = new Set();
  const delayFor = failures => Math.min(Math.max(100, retryMaxMs), Math.max(100, retryBaseMs) * 2 ** Math.min(failures - 1, 8));

  function connection(value, error = null) {
    if (disposed) return;
    // A successful concurrent presence request must not hide a failed input lane.
    if (value && (inputFailures || presenceFailures)) return;
    if (connected === value && (!error || connectionError?.message === error.message)) return;
    connected = value; connectionError = error;
    try { onConnection(value, error); } catch { /* UI reporting cannot break transport cleanup. */ }
  }
  function emit(message) { if (!disposed) onMessage(message); }
  function remember(set, id) {
    if (set.has(id)) return false;
    set.add(id);
    if (set.size > MAX_RECENT_IDS) set.delete(set.values().next().value);
    return true;
  }
  function forwardEvents(data) {
    const events = [...(Array.isArray(data.events) ? data.events : []), ...(Array.isArray(data.messages) ? data.messages : [])];
    for (const event of events) {
      if (!event || typeof event !== 'object' || !validId(event.type)) continue;
      if (event.id && !remember(eventIds, event.id)) continue;
      emit(event);
    }
  }
  function assertResponse(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw problem('Invalid world service response.', 'INVALID_HTTP_RESPONSE');
    if (data.type === 'error' || data.error) throw Object.assign(new Error(data.message || data.error || 'The action failed.'), {code: data.code});
    return data;
  }
  function request(path, body) {
    // Promise.resolve().then also catches synchronous adapter failures.
    return Promise.resolve().then(() => api(path, body, 'POST')).then(assertResponse);
  }
  function resetInputWorld(id) {
    if (activeWorldId === id) return;
    activeWorldId = id; worldGeneration++;
    inputFrames = []; inputResync = false;
    jumpEdges = []; lastQueuedJump = false; sentJump = false;
  }
  function applyState(data, sent, generation) {
    const player = data.player, worldId = data.worldId ?? player?.worldId;
    if (data.type !== 'state' || !player || !validId(worldId) || player.worldId !== worldId
      || (data.world && data.world.id !== worldId)) throw problem('Invalid authoritative player state.', 'INVALID_HTTP_RESPONSE');
    const sequence = validSeq(player.lastInputSeq) ? player.lastInputSeq : sent.seq;
    if (generation !== worldGeneration || sequence < lastAppliedSeq) return false;
    const crossing = data.crossing;
    if (crossing && (!crossing.world || !crossing.player || crossing.world.id !== worldId || crossing.player.worldId !== worldId)) {
      throw problem('Invalid authoritative crossing.', 'INVALID_HTTP_RESPONSE');
    }
    if (sent.predictPortals) {
      // Frame-tagged prediction can already be several doors ahead of this
      // response. Do not discard its queue or apply an extra camera transform.
      lastAppliedSeq = sequence;
      activeWorldId = worldId;
      if (data.inputReset) {
        inputFrames = []; inputResync = false; latestInput = null;
        pendingInput?.resolve({superseded: true, worldId}); pendingInput = null;
      }
      const players = Array.isArray(data.players) ? data.players.filter(p => p?.id !== player.id) : [];
      emit({...data, type:'state', worldId, players:[...players, player]});
      return true;
    }
    const crossingId = crossing && (crossing.player.lastCrossing?.id ?? `${crossing.player.id}:${crossing.player.crossingSeq ?? crossing.player.lastInputSeq ?? sent.seq}:${crossing.sourcePortalId}:${crossing.targetPortalId}`);
    const newCrossing = crossing && !crossingIds.has(crossingId);
    lastAppliedSeq = sequence;
    if (data.inputReset) {
      inputFrames = []; inputResync = false; latestInput = null; jumpEdges = [];
      pendingInput?.resolve({superseded: true, worldId}); pendingInput = null;
    }
    if (worldId !== activeWorldId || newCrossing) {
      // No old-space axes or jump edges may be applied in the destination frame.
      const changedWorld = worldId !== activeWorldId;
      if (changedWorld) resetInputWorld(worldId);
      if (latestInput?.worldId !== worldId || newCrossing) {
        latestInput = null; inputFrames = []; jumpEdges = [];
        pendingInput?.resolve({superseded: true, worldId}); pendingInput = null;
      }
      if (interest && (interest.baseWorldId !== worldId || newCrossing)) {
        interest = null; interestGeneration++; presenceFailures = 0; presenceRetryAt = -Infinity;
        pendingPresence?.resolve({superseded: true, worldId}); pendingPresence = null;
      }
    }
    if (newCrossing) {
      if (remember(crossingIds, crossingId)) {
        // Same-world crossings also transform the camera, exactly once.
        jumpEdges = []; lastQueuedJump = crossing.player.jumpHeld === true; sentJump = lastQueuedJump;
        emit({...crossing, type: 'crossed'});
      }
    }
    const players = Array.isArray(data.players) ? data.players.filter(p => p?.id !== player.id) : [];
    emit({...data, type: 'state', worldId, players: [...players, player]});
    return true;
  }

  function queueInput() {
    if (disposed || inputFlight || inputTimer !== null || (!latestInput && !inputFrames.length)) return;
    const delay = Math.max(0, inputSentAt + INPUT_INTERVAL - now(), inputRetryAt - now());
    inputTimer = schedule(() => { inputTimer = null; void flushInput(); }, delay);
  }
  async function flushInput() {
    if (inputFrames.length) return flushFrameInputs();
    if (disposed || inputFlight || !latestInput) return;
    inputFlight = true;
    const batch = pendingInput; pendingInput = null; activeInputBatch = batch;
    // A disconnected tab must not resume an old held direction after recovery.
    if (inputFailures && now() - latestInputAt > 350) {
      latestInput = {...latestInput, moveX: 0, moveZ: 0, jump: false, sprint: false};
      jumpEdges = []; lastQueuedJump = false;
    }
    const edge = jumpEdges.shift();
    const sent = {...latestInput, ...(edge ? {seq: edge.seq, jump: edge.jump} : {})};
    if (sent.seq === latestInput.seq) latestInput = null;
    const generation = worldGeneration, previousSentAt = inputSentAt;
    inputSentAt = now();
    const dt = Math.min(.1, Math.max(0, Number.isFinite(previousSentAt) ? (inputSentAt - previousSentAt) / 1000 : .1));
    try {
      const data = await request('/api/input', {...sent, dt});
      if (disposed) { batch?.resolve({disposed: true}); return; }
      applyState(data, sent, generation);
      sentJump = sent.jump; inputFailures = 0; inputRetryAt = -Infinity;
      connection(true); batch?.resolve(data);
    } catch (error) {
      if (disposed) { batch?.resolve({disposed: true}); return; }
      if (generation !== worldGeneration) { batch?.reject(error); return; }
      inputFailures++;
      inputRetryAt = now() + delayFor(inputFailures);
      // Conflict/network recovery retries the latest axes, never accumulated dt.
      if (generation === worldGeneration) {
        if (!latestInput) latestInput = sent;
        if (edge && !jumpEdges.some(e => e.seq === edge.seq)) jumpEdges.unshift(edge);
      }
      connection(false, error); batch?.reject(error);
    } finally {
      inputFlight = false; activeInputBatch = null;
      if (!disposed) queueInput();
    }
  }

  function boundInputFrames() {
    if (inputFrames.length <= MAX_INPUT_FRAMES) return;
    // Too much history means a stall/outage, not permission to skip motion silently.
    // Ask for a canonical rebase; the next response clears client replay too.
    const last = inputFrames.at(-1);
    inputFrames = [{...last, dt: 0, moveX: 0, moveZ: 0, jump: false, sprint: false}];
    inputResync = true;
  }
  async function flushFrameInputs() {
    if (disposed || inputFlight || !inputFrames.length) return;
    inputFlight = true;
    const batch = pendingInput; pendingInput = null; activeInputBatch = batch;
    const frames = inputFrames.splice(0, MAX_INPUT_FRAMES);
    const sent = frames.at(-1), generation = worldGeneration, resync = inputResync;
    inputResync = false;
    inputSentAt = now();
    try {
      const data = await request('/api/input', {worldId: frames[0].worldId, crossingSeq: frames[0].crossingSeq, ...(resync ? {resync: true} : {}), inputs: frames.map(({type, worldId, crossingSeq, predictPortals, ...sample}) => ({...sample,...(predictPortals?{worldId,crossingSeq}:{})}))});
      if (disposed) { batch?.resolve({disposed: true}); return; }
      applyState(data, sent, generation);
      inputFailures = 0; inputRetryAt = -Infinity;
      connection(true); batch?.resolve(data);
    } catch (error) {
      if (disposed) { batch?.resolve({disposed: true}); return; }
      if (generation !== worldGeneration) { batch?.reject(error); return; }
      inputFailures++; inputRetryAt = now() + delayFor(inputFailures);
      // The server may have committed before a response was lost. Retry exact
      // samples with the same sequence IDs; never reassign dt or jump edges.
      inputFrames = [...frames, ...inputFrames];
      inputResync ||= resync;
      boundInputFrames();
      connection(false, error); batch?.reject(error);
    } finally {
      inputFlight = false; activeInputBatch = null;
      if (!disposed) queueInput();
    }
  }

  function queuePresence() {
    if (disposed || presenceFlight || presenceTimer !== null || !interest) return;
    const delay = Math.max(0, presenceSentAt + PRESENCE_INTERVAL - now(), presenceRetryAt - now());
    presenceTimer = schedule(() => { presenceTimer = null; void flushPresence(); }, delay);
  }
  async function flushPresence() {
    if (disposed || presenceFlight || !interest) return;
    if (!isVisible()) {
      presenceTimer = schedule(() => { presenceTimer = null; void flushPresence(); }, PRESENCE_INTERVAL);
      return;
    }
    presenceFlight = true; presenceSentAt = now();
    const sent = interest, generation = interestGeneration;
    const batch = pendingPresence; pendingPresence = null; activePresenceBatch = batch;
    try {
      const data = await request('/api/presence', {...sent, ...(eventCursor === undefined ? {} : {eventCursor})});
      if (disposed) { batch?.resolve({disposed: true}); return; }
      if (data.type !== 'presence' || !validSeq(data.snapshotSeq) || !Array.isArray(data.worldIds) || !Array.isArray(data.worlds)) {
        throw problem('Invalid presence snapshot.', 'INVALID_HTTP_RESPONSE');
      }
      presenceFailures = 0; presenceRetryAt = -Infinity;
      connection(true);
      if (generation === interestGeneration && data.requestSeq === sent.requestSeq && data.snapshotSeq > lastSnapshotSeq && isVisible()) {
        lastSnapshotSeq = data.snapshotSeq;
        // Forward the atomic roster unchanged; event envelopes are separately deduped.
        emit(data); forwardEvents(data);
        if (Number.isFinite(data.eventCursor)) eventCursor = Math.max(eventCursor ?? 0, data.eventCursor);
      }
      batch?.resolve(data);
    } catch (error) {
      if (disposed) { batch?.resolve({disposed: true}); return; }
      if (generation !== interestGeneration) { batch?.reject(error); return; }
      if (error.code === 'INVALID_PRESENCE_INTEREST' || error.code === 'PRESENCE_RATE_LIMIT') {
        // Presence can race an input crossing. A rejected read-only camera
        // interest must never stop movement or roll back a predicted doorway.
        presenceFailures = 0; presenceRetryAt = now() + PRESENCE_INTERVAL;
        emit({type:'error',code:error.code,message:error.message});
        batch?.reject(error); return;
      }
      presenceFailures++; presenceRetryAt = now() + delayFor(presenceFailures);
      connection(false, error); batch?.reject(error);
    } finally {
      presenceFlight = false; activePresenceBatch = null;
      if (!disposed) queuePresence();
    }
  }

  function operation(message) {
    const config = {
      chat: ['/api/chat', {text: message.text}],
      interact: ['/api/interact', {entityId: message.entityId}],
      selectPortal: ['/api/portal-selection', {portalId: message.portalId}],
    }[message.type];
    if (!config) return observed(Promise.reject(problem(`Unsupported HTTP world action: ${message.type ?? 'unknown'}`, 'UNSUPPORTED_HTTP_ACTION')));
    const wait = deferred(), generation = worldGeneration;
    actionWaiters.add(wait);
    void request(...config).then(data => {
      if (disposed) { wait.resolve({disposed: true}); return; }
      connection(true);
      if (data.world) emit({type: 'world', world: data.world, ...(generation === worldGeneration && data.player ? {player: data.player} : {})});
      forwardEvents(data);
      if (message.type === 'selectPortal') {
        if (!validId(data.portalId)) throw problem('Doorway selection was not confirmed.', 'INVALID_HTTP_RESPONSE');
        if (generation === worldGeneration) emit({type: 'portalSelected', portalId: data.portalId});
      } else if (data.type && data.type !== 'world') {
        if (!data.id || remember(eventIds, data.id)) emit(data);
      }
      wait.resolve(data);
    }).catch(error => {
      // Validation/cooldown errors are successful contact with the service,
      // not evidence that movement and presence have disconnected.
      if (!disposed && (!Number.isFinite(error.status) || error.status >= 500 || error.status === 401)) connection(false, error);
      wait.reject(error);
    }).finally(() => actionWaiters.delete(wait));
    return wait.promise;
  }

  return {
    send(message) {
      if (disposed) return observed(Promise.reject(problem('World transport is closed.', 'TRANSPORT_DISPOSED')));
      if (!message || typeof message !== 'object') return observed(Promise.reject(new TypeError('World message must be an object')));
      if (message.type === 'input') {
        if (!validId(message.worldId) || !validSeq(message.seq)) return observed(Promise.reject(problem('Input requires worldId and sequence.', 'INVALID_INPUT')));
        if (message.seq <= lastQueuedSeq) return Promise.resolve({superseded: true});
        if (message.predictPortals && Object.hasOwn(message, 'dt')) { activeWorldId ??= message.worldId; } else if (activeWorldId && message.worldId !== activeWorldId && inputFlight) {
          // A newer externally established world makes old in-flight responses stale.
          resetInputWorld(message.worldId);
        } else if (!activeWorldId || message.worldId !== activeWorldId) resetInputWorld(message.worldId);
        const input = {type: 'input', worldId: message.worldId, ...normalizeInput(message), ...(Object.hasOwn(message, 'dt') ? {crossingSeq: Number.isSafeInteger(message.crossingSeq) ? message.crossingSeq : 0,...(message.predictPortals?{predictPortals:true}:{})} : {})};
        if (Object.hasOwn(message, 'dt')) {
          if (!Number.isFinite(message.dt) || message.dt < 0 || message.dt > .055) return observed(Promise.reject(problem('Input duration must be between 0 and .055 seconds.', 'INVALID_INPUT')));
          lastQueuedSeq = input.seq;
          inputFrames.push({...input, dt: message.dt});
          boundInputFrames();
          latestInputAt = now(); pendingInput ??= deferred();
          const promise = pendingInput.promise; queueInput(); return promise;
        }
        lastQueuedSeq = input.seq;
        if (input.jump !== lastQueuedJump) {
          jumpEdges.push({seq: input.seq, jump: input.jump});
          // Keep a recent valid edge sequence under pathological key spam. Start
          // from the last server-held state so retained presses still have edges.
          if (jumpEdges.length > MAX_JUMP_EDGES) {
            jumpEdges = jumpEdges.slice(-MAX_JUMP_EDGES);
            if (jumpEdges[0].jump === sentJump) jumpEdges.shift();
          }
        }
        lastQueuedJump = input.jump; latestInput = input; latestInputAt = now();
        pendingInput ??= deferred();
        const promise = pendingInput.promise; queueInput(); return promise;
      }
      if (message.type === 'presenceInterest') {
        if (!validId(message.baseWorldId) || !validSeq(message.requestSeq) || !Array.isArray(message.visiblePortalIds)) return observed(Promise.reject(problem('Invalid presence interest.', 'INVALID_PRESENCE_INTEREST')));
        if (interest && message.requestSeq <= interest.requestSeq) return Promise.resolve({superseded: true});
        interest = {baseWorldId: message.baseWorldId, cameraPortalId: message.cameraPortalId ?? null, visiblePortalIds: [...message.visiblePortalIds], requestSeq: message.requestSeq};
        interestGeneration++; pendingPresence ??= deferred();
        const promise = pendingPresence.promise; queuePresence(); return promise;
      }
      return operation(message);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (inputTimer !== null) cancel(inputTimer);
      if (presenceTimer !== null) cancel(presenceTimer);
      inputTimer = presenceTimer = null; latestInput = interest = null; inputFrames = []; jumpEdges = [];
      pendingInput?.resolve({disposed: true}); pendingPresence?.resolve({disposed: true});
      activeInputBatch?.resolve({disposed: true}); activePresenceBatch?.resolve({disposed: true});
      pendingInput = pendingPresence = null;
      for (const wait of actionWaiters) wait.resolve({disposed: true});
      actionWaiters.clear(); eventIds.clear(); crossingIds.clear();
    },
  };
}
